import { observable } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';
import { Session } from 'next-auth';
import { adminService } from '@/lib/services';
import {
  DashboardOverview,
  User,
  ChatMessage,
  AuditLogEntry,
  Payment,
  Subscription,
  PaginatedResponse,
  SubscriptionRevenue,
  FlaggedResponse,
  ChatHistory,
  AuditLogResponse,
} from '@/lib/services/admin.service';

// Types
export interface AdminState {
  overview: {
    data: DashboardOverview | null;
    isLoading: boolean;
    error: Error | null;
  };
  users: {
    data: User[];
    isLoading: boolean;
    error: Error | null;
    sortField: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    selectedUser: User | null;
    selectedUserId: string | null;
    showChatHistory: boolean;
    userDetails: {
      data: User | null;
      isLoading: boolean;
      error: Error | null;
      isEditing: boolean;
    };
  };
  chatHistory: {
    data: Record<string, ChatHistory>;
    isLoading: boolean;
    error: Error | null;
  };
  auditLogs: {
    data: AuditLogEntry[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    } | null;
    isLoading: boolean;
    error: Error | null;
    sortField: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    currentPage: number;
  };
  payments: {
    data: PaginatedResponse<Payment> | null;
    isLoading: boolean;
    error: Error | null;
    sortField: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    currentPage: number;
    pageSize: number;
    statusFilter: string;
  };
  subscriptions: {
    data: PaginatedResponse<Subscription> | null;
    revenue: SubscriptionRevenue | null;
    isLoading: boolean;
    error: Error | null;
  };
  flaggedResponses: {
    data: PaginatedResponse<FlaggedResponse> | null;
    isLoading: boolean;
    error: Error | null;
  };
  session: Session | null;
}

// Initial state
const initialState: AdminState = {
  overview: {
    data: null,
    isLoading: false,
    error: null,
  },
  users: {
    data: [],
    isLoading: false,
    error: null,
    sortField: 'name',
    sortOrder: 'asc',
    searchQuery: '',
    selectedUser: null,
    selectedUserId: null,
    showChatHistory: false,
    userDetails: {
      data: null,
      isLoading: false,
      error: null,
      isEditing: false,
    },
  },
  chatHistory: {
    data: {},
    isLoading: false,
    error: null,
  },
  auditLogs: {
    data: [],
    meta: null,
    isLoading: false,
    error: null,
    sortField: 'timestamp',
    sortOrder: 'desc',
    searchQuery: '',
    currentPage: 1,
  },
  payments: {
    data: null,
    isLoading: false,
    error: null,
    sortField: 'createdAt',
    sortOrder: 'desc',
    searchQuery: '',
    currentPage: 1,
    pageSize: 10,
    statusFilter: 'all',
  },
  subscriptions: {
    data: null,
    revenue: null,
    isLoading: false,
    error: null,
  },
  flaggedResponses: {
    data: null,
    isLoading: false,
    error: null,
  },
  session: null,
};

// Create the observable store
export const adminState = observable<AdminState>(initialState);

// Action creators
export const adminActions = {
  // Session management
  setSession: (session: Session | null) => {
    logger.debug('[adminStore] Setting session', { session });
    adminState.session.set(session);
  },

  // Overview actions
  setOverview: (overview: DashboardOverview | null) => {
    logger.debug('[adminStore] Setting overview', { overview });
    adminState.overview.data.set(overview);
  },

  startOverviewLoading: () => {
    logger.debug('[adminStore] Starting overview loading');
    adminState.overview.isLoading.set(true);
    adminState.overview.error.set(null);
  },

  setOverviewError: (error: Error) => {
    logger.debug('[adminStore] Setting overview error', { error });
    adminState.overview.isLoading.set(false);
    adminState.overview.error.set(error);
  },

  completeOverviewLoading: () => {
    logger.debug('[adminStore] Completing overview loading');
    adminState.overview.isLoading.set(false);
  },

  // Users actions
  setUsers: (users: User[]) => {
    logger.debug('[adminStore] Setting users', { count: users.length });
    adminState.users.data.set(users);
  },

  startUsersLoading: () => {
    logger.debug('[adminStore] Starting users loading');
    adminState.users.isLoading.set(true);
    adminState.users.error.set(null);
  },

  setUsersError: (error: Error) => {
    logger.debug('[adminStore] Setting users error', { error });
    adminState.users.isLoading.set(false);
    adminState.users.error.set(error);
  },

  completeUsersLoading: () => {
    logger.debug('[adminStore] Completing users loading');
    adminState.users.isLoading.set(false);
  },

  setUserSortField: (field: string) => {
    adminState.users.sortField.set(field);
  },

  setUserSortOrder: (order: 'asc' | 'desc') => {
    adminState.users.sortOrder.set(order);
  },

  setUserSearchQuery: (query: string) => {
    adminState.users.searchQuery.set(query);
  },

  toggleUserSort: (field: string) => {
    const currentField = adminState.users.sortField.get();
    const currentOrder = adminState.users.sortOrder.get();

    if (currentField === field) {
      adminState.users.sortOrder.set(currentOrder === 'asc' ? 'desc' : 'asc');
    } else {
      adminState.users.sortField.set(field);
      adminState.users.sortOrder.set('asc');
    }
  },

  setSelectedUser: (user: User | null) => {
    adminState.users.selectedUser.set(user);
  },

  setSelectedUserId: (userId: string | null) => {
    adminState.users.selectedUserId.set(userId);
  },

  setShowChatHistory: (show: boolean) => {
    adminState.users.showChatHistory.set(show);
  },

  // Chat history actions
  setChatHistory: (userId: string, history: ChatHistory) => {
    logger.debug('[adminStore] Setting chat history', {
      userId,
      count: history.responses.length,
    });
    const currentData = adminState.chatHistory.data.get();
    adminState.chatHistory.data.set({ ...currentData, [userId]: history });
  },

  startChatHistoryLoading: () => {
    logger.debug('[adminStore] Starting chat history loading');
    adminState.chatHistory.isLoading.set(true);
    adminState.chatHistory.error.set(null);
  },

  setChatHistoryError: (error: Error) => {
    logger.debug('[adminStore] Setting chat history error', { error });
    adminState.chatHistory.isLoading.set(false);
    adminState.chatHistory.error.set(error);
  },

  completeChatHistoryLoading: () => {
    logger.debug('[adminStore] Completing chat history loading');
    adminState.chatHistory.isLoading.set(false);
  },

  // Audit logs actions
  loadAuditLogs: async (params: {
    page?: number;
    limit?: number;
    entityType?: string;
    action?: string;
    userId?: string;
    startDate?: Date;
    endDate?: Date;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
    searchQuery?: string;
  }) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startAuditLogsLoading();
      
      // Update current page in state
      if (params.page) {
        adminState.auditLogs.currentPage.set(params.page);
      }
      
      // Apply sort if provided
      if (params.sortField) {
        adminState.auditLogs.sortField.set(params.sortField);
      }
      
      if (params.sortOrder) {
        adminState.auditLogs.sortOrder.set(params.sortOrder);
      }
      
      // Get current state values for parameters not provided
      const currentPage = adminState.auditLogs.currentPage.get();
      const sortField = adminState.auditLogs.sortField.get();
      const sortOrder = adminState.auditLogs.sortOrder.get();
      const searchQuery = adminState.auditLogs.searchQuery.get();
      
      const response = await adminService.getAuditLogs(session, {
        page: params.page || currentPage,
        limit: params.limit || 12,
        entityType: params.entityType,
        action: params.action,
        userId: params.userId,
        startDate: params.startDate,
        endDate: params.endDate,
        sortField: params.sortField || sortField,
        sortOrder: params.sortOrder || sortOrder,
        searchQuery: params.searchQuery !== undefined ? params.searchQuery : searchQuery,
      });
      
      adminState.auditLogs.data.set(response.items);
      adminState.auditLogs.meta.set(response.meta);
      
      return response;
    } catch (error) {
      logger.error('[adminStore] Failed to get audit logs:', error);
      adminActions.setAuditLogsError(error as Error);
      throw error;
    } finally {
      adminActions.completeAuditLogsLoading();
    }
  },

  getAuditLogs: async () => {
    return adminActions.loadAuditLogs({});
  },

  setAuditLogs: (response: AuditLogResponse) => {
    adminState.auditLogs.data.set(response.items);
    adminState.auditLogs.meta.set(response.meta);
  },

  startAuditLogsLoading: () => {
    logger.debug('[adminStore] Starting audit logs loading');
    adminState.auditLogs.isLoading.set(true);
    adminState.auditLogs.error.set(null);
  },

  setAuditLogsError: (error: Error) => {
    logger.debug('[adminStore] Setting audit logs error', { error });
    adminState.auditLogs.isLoading.set(false);
    adminState.auditLogs.error.set(error);
  },

  completeAuditLogsLoading: () => {
    logger.debug('[adminStore] Completing audit logs loading');
    adminState.auditLogs.isLoading.set(false);
  },

  // Payments actions
  setPayments: (payments: PaginatedResponse<Payment> | null) => {
    logger.debug('[adminStore] Setting payments', {
      count: payments?.data.length,
    });
    adminState.payments.data.set(payments);
  },

  startPaymentsLoading: () => {
    logger.debug('[adminStore] Starting payments loading');
    adminState.payments.isLoading.set(true);
    adminState.payments.error.set(null);
  },

  setPaymentsError: (error: Error) => {
    logger.debug('[adminStore] Setting payments error', { error });
    adminState.payments.isLoading.set(false);
    adminState.payments.error.set(error);
  },

  completePaymentsLoading: () => {
    logger.debug('[adminStore] Completing payments loading');
    adminState.payments.isLoading.set(false);
  },

  setPaymentSortField: (field: string) => {
    adminState.payments.sortField.set(field);
  },

  setPaymentSortOrder: (order: 'asc' | 'desc') => {
    adminState.payments.sortOrder.set(order);
  },

  setPaymentSearchQuery: (query: string) => {
    adminState.payments.searchQuery.set(query);
  },

  setPaymentPage: (page: number) => {
    logger.debug('[adminStore] Setting payment page', { page });
    adminState.payments.currentPage.set(page);
  },

  setPaymentPageSize: (size: number) => {
    adminState.payments.pageSize.set(size);
  },

  togglePaymentSort: (field: string) => {
    const currentField = adminState.payments.sortField.get();
    const currentOrder = adminState.payments.sortOrder.get();

    if (currentField === field) {
      adminState.payments.sortOrder.set(
        currentOrder === 'asc' ? 'desc' : 'asc'
      );
    } else {
      adminState.payments.sortField.set(field);
      adminState.payments.sortOrder.set('asc');
    }
  },

  // Subscriptions actions
  setSubscriptions: (subscriptions: PaginatedResponse<Subscription> | null) => {
    logger.debug('[adminStore] Setting subscriptions', {
      count: subscriptions?.data.length,
    });
    adminState.subscriptions.data.set(subscriptions);
  },

  setSubscriptionRevenue: (revenue: SubscriptionRevenue | null) => {
    logger.debug('[adminStore] Setting subscription revenue', { revenue });
    adminState.subscriptions.revenue.set(revenue);
  },

  startSubscriptionsLoading: () => {
    logger.debug('[adminStore] Starting subscriptions loading');
    adminState.subscriptions.isLoading.set(true);
    adminState.subscriptions.error.set(null);
  },

  setSubscriptionsError: (error: Error) => {
    logger.debug('[adminStore] Setting subscriptions error', { error });
    adminState.subscriptions.isLoading.set(false);
    adminState.subscriptions.error.set(error);
  },

  completeSubscriptionsLoading: () => {
    logger.debug('[adminStore] Completing subscriptions loading');
    adminState.subscriptions.isLoading.set(false);
  },

  // Flagged responses actions
  setFlaggedResponses: (
    responses: PaginatedResponse<FlaggedResponse> | null
  ) => {
    logger.debug('[adminStore] Setting flagged responses', {
      count: responses?.data.length,
    });
    adminState.flaggedResponses.data.set(responses);
  },

  startFlaggedResponsesLoading: () => {
    logger.debug('[adminStore] Starting flagged responses loading');
    adminState.flaggedResponses.isLoading.set(true);
    adminState.flaggedResponses.error.set(null);
  },

  setFlaggedResponsesError: (error: Error) => {
    logger.debug('[adminStore] Setting flagged responses error', { error });
    adminState.flaggedResponses.isLoading.set(false);
    adminState.flaggedResponses.error.set(error);
  },

  completeFlaggedResponsesLoading: () => {
    logger.debug('[adminStore] Completing flagged responses loading');
    adminState.flaggedResponses.isLoading.set(false);
  },

  reset: () => {
    logger.debug('[adminStore] Resetting state');
    adminState.set(initialState);
  },

  // Service-connected actions
  getOverview: async () => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startOverviewLoading();
      const overview = await adminService.getOverview(session);
      adminActions.setOverview(overview);
      return overview;
    } catch (error) {
      logger.error('[adminStore] Failed to get overview:', error);
      adminActions.setOverviewError(error as Error);
      throw error;
    } finally {
      adminActions.completeOverviewLoading();
    }
  },

  getUsers: async () => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startUsersLoading();
      const users = await adminService.getUsers(session);
      adminActions.setUsers(users);
      return users;
    } catch (error) {
      logger.error('[adminStore] Failed to get users:', error);
      adminActions.setUsersError(error as Error);
      throw error;
    } finally {
      adminActions.completeUsersLoading();
    }
  },

  getChatHistory: async (userId: string) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startChatHistoryLoading();
      const messages = await adminService.getChatHistory(userId, session);
      const history: ChatHistory = {
        id: userId,
        title: 'Chat History',
        description: `Chat history for user ${userId}`,
        responses: messages.map((msg) => ({
          question: msg.role === 'user' ? msg.content : '',
          answer: msg.role === 'assistant' ? msg.content : '',
          timestamp: msg.timestamp,
        })),
        createdAt: new Date().toISOString(),
      };
      adminActions.setChatHistory(userId, history);
      return history;
    } catch (error) {
      logger.error('[adminStore] Failed to get chat history:', error);
      adminActions.setChatHistoryError(error as Error);
      throw error;
    } finally {
      adminActions.completeChatHistoryLoading();
    }
  },

  async getPayments(page = 1, limit = 10, status?: string, startDate?: Date, endDate?: Date) {
    try {
      const session = adminState.session.get();
      if (!session) {
        console.error('No session available for fetching payments');
        adminState.payments.error.set(new Error('Authentication required'));
        adminState.payments.isLoading.set(false);
        return;
      }

      adminState.payments.isLoading.set(true);
      adminState.payments.error.set(null);

      console.log(`Fetching payments: page=${page}, limit=${limit}, status=${status || 'all'}`);
      
      const payments = await adminService.getPayments(
        session,
        page,
        limit,
        status,
        startDate,
        endDate
      );

      console.log('Payments fetched successfully:', payments);
      
      adminState.payments.data.set(payments);
      adminState.payments.isLoading.set(false);
      return payments;
    } catch (error) {
      console.error('Error fetching payments:', error);
      adminState.payments.error.set(error instanceof Error ? error : new Error('Failed to fetch payments'));
      adminState.payments.isLoading.set(false);
      
      // Return empty data structure instead of throwing
      return {
        data: [],
        meta: { total: 0, page, limit, totalPages: 0 },
      };
    }
  },

  getSubscriptions: async (page: number = 1, limit: number = 10) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startSubscriptionsLoading();
      const [subscriptions, revenue] = await Promise.all([
        adminService.getSubscriptions(session, page, limit),
        adminService.getSubscriptionRevenue(session),
      ]);
      adminActions.setSubscriptions(subscriptions);
      adminActions.setSubscriptionRevenue(revenue);
      return { subscriptions, revenue };
    } catch (error) {
      logger.error('[adminStore] Failed to get subscriptions:', error);
      adminActions.setSubscriptionsError(error as Error);
      throw error;
    } finally {
      adminActions.completeSubscriptionsLoading();
    }
  },

  getFlaggedResponses: async () => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startFlaggedResponsesLoading();
      const flaggedResponses = await adminService.getFlaggedResponses(session);
      // Transform to paginated response format for consistency
      const paginatedResponse = {
        data: flaggedResponses,
        meta: {
          total: flaggedResponses.length,
          page: 1,
          limit: flaggedResponses.length,
          totalPages: 1,
        },
      };
      adminActions.setFlaggedResponses(paginatedResponse);
      return flaggedResponses;
    } catch (error) {
      logger.error('[adminStore] Failed to get flagged responses:', error);
      adminActions.setFlaggedResponsesError(error as Error);
      throw error;
    } finally {
      adminActions.completeFlaggedResponsesLoading();
    }
  },

  // Add selectors for filtered data
  getFilteredUsers: () => {
    const users = adminState.users.data.get();
    const searchQuery = adminState.users.searchQuery.get().toLowerCase();
    const sortField = adminState.users.sortField.get();
    const sortOrder = adminState.users.sortOrder.get();

    let filtered = users;

    if (searchQuery) {
      filtered = users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchQuery) ||
          user.email.toLowerCase().includes(searchQuery)
      );
    }

    return [...filtered].sort((a, b) => {
      const aValue = a[sortField as keyof typeof a];
      const bValue = b[sortField as keyof typeof b];
      const modifier = sortOrder === 'asc' ? 1 : -1;
      return aValue < bValue ? -1 * modifier : 1 * modifier;
    });
  },

  getFilteredPayments: () => {
    const payments = adminState.payments.data.get()?.data || [];
    const searchQuery = adminState.payments.searchQuery.get().toLowerCase();
    const statusFilter = adminState.payments.statusFilter.get();

    return payments.filter((payment: Payment) => {
      const matchesSearch = payment.id.toLowerCase().includes(searchQuery) ||
        payment.userId.toLowerCase().includes(searchQuery);
      const matchesStatus = !statusFilter || statusFilter === 'all' || payment.status.toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  },

  // User details actions
  startUserDetailsLoading: () => {
    logger.debug('[adminStore] Starting user details loading');
    adminState.users.userDetails.isLoading.set(true);
    adminState.users.userDetails.error.set(null);
  },

  setUserDetailsError: (error: Error) => {
    logger.debug('[adminStore] Setting user details error', { error });
    adminState.users.userDetails.isLoading.set(false);
    adminState.users.userDetails.error.set(error);
  },

  completeUserDetailsLoading: () => {
    logger.debug('[adminStore] Completing user details loading');
    adminState.users.userDetails.isLoading.set(false);
  },

  setUserDetails: (user: User | null) => {
    logger.debug('[adminStore] Setting user details', { user });
    adminState.users.userDetails.data.set(user);
  },

  setUserDetailsEditing: (isEditing: boolean) => {
    logger.debug('[adminStore] Setting user details editing', { isEditing });
    adminState.users.userDetails.isEditing.set(isEditing);
  },

  fetchUserDetails: async (userId: string) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startUserDetailsLoading();
      const users = await adminService.getUsers(session);
      const userDetails = users.find((u) => u.id === userId);

      if (userDetails) {
        adminActions.setUserDetails({
          ...userDetails,
          lastLogin: userDetails.lastLogin?.toString(),
          createdAt: userDetails.createdAt?.toString(),
        });
      } else {
        adminActions.setUserDetails(null);
      }
    } catch (error) {
      logger.error('[adminStore] Failed to fetch user details:', error);
      adminActions.setUserDetailsError(error as Error);
    } finally {
      adminActions.completeUserDetailsLoading();
    }
  },

  updateUserDetails: async (userId: string, userData: Partial<User>) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      adminActions.startUserDetailsLoading();
      await adminService.updateUser(userId, userData, session);
      await adminActions.fetchUserDetails(userId);
      return true;
    } catch (error) {
      logger.error('[adminStore] Failed to update user details:', error);
      adminActions.setUserDetailsError(error as Error);
      throw error;
    } finally {
      adminActions.completeUserDetailsLoading();
    }
  },

  // Audit Log Actions
  setAuditLogSearchQuery: (query: string) => {
    adminState.auditLogs.searchQuery.set(query);
    
    // Debounce search to avoid too many requests
    if (window.auditLogSearchTimeout) {
      clearTimeout(window.auditLogSearchTimeout);
    }
    
    window.auditLogSearchTimeout = setTimeout(() => {
      adminActions.loadAuditLogs({
        searchQuery: query,
        page: 1, // Reset to first page on new search
      });
    }, 500);
  },

  toggleAuditLogSort: (field: string) => {
    let newSortOrder: 'asc' | 'desc';
    
    if (adminState.auditLogs.sortField.get() === field) {
      // Toggle sort order if the same field
      newSortOrder = adminState.auditLogs.sortOrder.get() === 'asc' ? 'desc' : 'asc';
      adminState.auditLogs.sortOrder.set(newSortOrder);
    } else {
      // Set new field and default to desc
      adminState.auditLogs.sortField.set(field);
      newSortOrder = 'desc';
      adminState.auditLogs.sortOrder.set(newSortOrder);
    }
    
    // Reload with new sort
    adminActions.loadAuditLogs({
      sortField: field,
      sortOrder: newSortOrder,
      page: adminState.auditLogs.currentPage.get(),
    });
  },

  // User Actions
  disableUser: async (userId: string) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      await adminService.disableUser(userId, session);
      // Refresh users list after deletion
      await adminActions.getUsers();
    } catch (error) {
      logger.error('[adminStore] Failed to disable user:', error);
      throw error;
    }
  },

  issueRefund: async (paymentId: string, amount?: number, reason?: string) => {
    const session = adminState.session.get();
    if (!session) return;

    try {
      await adminService.issueRefund(session, paymentId, amount, reason);
      // Refresh payments list after refund
      await adminActions.getPayments();
    } catch (error) {
      logger.error('[adminStore] Failed to issue refund:', error);
      throw error;
    }
  },

  // Payment actions
  setPaymentFilter: (status: string) => {
    logger.debug('[adminStore] Setting payment filter', { status });
    adminState.payments.statusFilter.set(status);
  },
};

// Selectors
export const adminSelectors = {
  getOverview: () => adminState.overview.data.get(),
  isOverviewLoading: () => adminState.overview.isLoading.get(),
  getOverviewError: () => adminState.overview.error.get(),

  getUsers: () => adminState.users.data.get(),
  isUsersLoading: () => adminState.users.isLoading.get(),
  getUsersError: () => adminState.users.error.get(),

  getChatHistory: (userId: string) => adminState.chatHistory.data.get()[userId],
  isChatHistoryLoading: () => adminState.chatHistory.isLoading.get(),
  getChatHistoryError: () => adminState.chatHistory.error.get(),

  getAuditLogs: () => adminState.auditLogs.data.get(),
  getAuditLogsMeta: () => adminState.auditLogs.meta.get(),
  isAuditLogsLoading: () => adminState.auditLogs.isLoading.get(),
  getAuditLogsError: () => adminState.auditLogs.error.get(),

  getPayments: () => adminState.payments.data.get(),
  getPaymentsLoading: () => adminState.payments.isLoading.get(),
  getPaymentsError: () => adminState.payments.error.get(),
  getPaymentSearchQuery: () => adminState.payments.searchQuery.get(),
  getPaymentStatusFilter: () => adminState.payments.statusFilter.get() || 'all',
  getPaymentPage: () => adminState.payments.currentPage.get(),
  getPaymentPageSize: () => adminState.payments.pageSize.get(),
  getPaymentSortField: () => adminState.payments.sortField.get(),
  getPaymentSortOrder: () => adminState.payments.sortOrder.get(),
  getFilteredPayments: () => {
    const payments = adminState.payments.data.get()?.data || [];
    const searchQuery = adminState.payments.searchQuery.get().toLowerCase();
    const statusFilter = adminState.payments.statusFilter.get();

    return payments.filter((payment: Payment) => {
      const matchesSearch = payment.id.toLowerCase().includes(searchQuery) ||
        payment.userId.toLowerCase().includes(searchQuery);
      const matchesStatus = !statusFilter || statusFilter === 'all' || payment.status.toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  },

  getSubscriptions: () => adminState.subscriptions.data.get(),
  getSubscriptionRevenue: () => adminState.subscriptions.revenue.get(),
  isSubscriptionsLoading: () => adminState.subscriptions.isLoading.get(),
  getSubscriptionsError: () => adminState.subscriptions.error.get(),

  getFlaggedResponses: () => adminState.flaggedResponses.data.get(),
  isFlaggedResponsesLoading: () => adminState.flaggedResponses.isLoading.get(),
  getFlaggedResponsesError: () => adminState.flaggedResponses.error.get(),

  getSession: () => adminState.session.get(),

  // Audit log sorting and filtering selectors
  getAuditLogSortField: () => adminState.auditLogs.sortField.get(),
  getAuditLogSortOrder: () => adminState.auditLogs.sortOrder.get(),
  getAuditLogSearchQuery: () => adminState.auditLogs.searchQuery.get(),
  getAuditLogCurrentPage: () => adminState.auditLogs.currentPage.get(),

  // Users selectors
  getUserSortField: () => adminState.users.sortField.get(),
  getUserSortOrder: () => adminState.users.sortOrder.get(),
  getUserSearchQuery: () => adminState.users.searchQuery.get(),
  getSelectedUser: () => adminState.users.selectedUser.get(),
  getSelectedUserId: () => adminState.users.selectedUserId.get(),
  getShowChatHistory: () => adminState.users.showChatHistory.get(),
  getFilteredUsers: () => adminActions.getFilteredUsers(),

  // User details selectors
  getUserDetails: () => adminState.users.userDetails.data.get(),
  isUserDetailsLoading: () => adminState.users.userDetails.isLoading.get(),
  getUserDetailsError: () => adminState.users.userDetails.error.get(),
  isUserDetailsEditing: () => adminState.users.userDetails.isEditing.get(),

  // Overview-specific selectors
  getOverviewStats: () => adminState.overview.data.get()?.stats || {
    totalUsers: 0,
    activeUsers: 0,
    dailyActiveUsers: 0,
    monthlyActiveUsers: 0,
    averageSessionDuration: 0,
    userEngagement: 0,
    retentionRate: 0,
    totalQuestions: 0,
    totalRevenue: 0,
    revenuePerUser: 0,
    conversionRate: 0,
    activeSubscriptions: 0,
  },

  getOverviewMetrics: () => adminState.overview.data.get()?.metrics || {
    userGrowth: 0,
    revenueGrowth: 0,
    activeUsersGrowth: 0,
    timeDistribution: {
      hourly: [],
      daily: [],
      weekly: [],
      monthly: [],
    },
    interactions: [],
    responseTime: {
      lambdaTime: 0,
      frontendTime: 0,
    },
    conversationLength: {
      averageLength: 0,
      distribution: [],
    },
  },

  getOverviewAnalytics: () => adminState.overview.data.get()?.analytics || {
    topics: [],
    feedback: {
      positiveCount: 0,
      negativeCount: 0,
      totalCount: 0,
      feedbackRate: 0,
      topReasons: [],
    },
  },

  getOverviewSystemHealth: () => adminState.overview.data.get()?.systemHealth || {
    status: 'unknown',
    uptime: 0,
    errorRate: 0,
    responseTime: 0,
  },

  getOverviewRecentActivity: () => adminState.overview.data.get()?.recentActivity || [],
};
